import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type Row,
} from '@tanstack/react-table';
import {
  ChevronDown,
  ExternalLink,
  Eye,
  Inbox,
  MoreVertical,
  Play,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { DataTable } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import type { ScrapingQueueItem } from '@repo/types';
import { ScrapingStatusBadge } from './ScrapingStatusBadge';
import { detectPlatform, deriveJobTitle, prettySourceUrl } from '@/lib/scraping-source';

/** Small brand-ish badge naming the source job board (Indeed, Bayt, …). */
function PlatformBadge({ url, className }: { url: string; className?: string }) {
  const platform = detectPlatform(url);
  return (
    <span
      className={cn(
        'inline-flex max-w-[120px] shrink-0 items-center truncate rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        platform.className,
        className
      )}
      title={platform.label}
    >
      {platform.label}
    </span>
  );
}

interface ScrapingQueueTableProps {
  items: ScrapingQueueItem[];
  onRetry?: (id: string) => void;
  onDelete?: (id: string) => void;
  onView?: (item: ScrapingQueueItem) => void;
  onStart?: (id: string) => void;
}

// Explains why a *completed* scrape did or didn't add a job.
const OUTCOME_CONFIG: Record<
  NonNullable<ScrapingQueueItem['outcome']>,
  { label: string; className: string }
> = {
  inserted: {
    label: 'New job',
    className:
      'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  duplicate: {
    label: 'Duplicate · draft',
    className: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  exists: {
    label: 'Already existed',
    className: 'border-border bg-muted text-muted-foreground',
  },
};

function ScrapingOutcomeBadge({
  outcome,
  className: extra,
}: {
  outcome: NonNullable<ScrapingQueueItem['outcome']>;
  className?: string;
}) {
  const { label, className } = OUTCOME_CONFIG[outcome];
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium',
        className,
        extra
      )}
    >
      {label}
    </span>
  );
}

// Rows doing live work carry a faint persistent tint, so an in-progress scrape is
// easy to spot in the list at a glance (the status badge also shows a live "ping").
const ACTIVE_ROW: Partial<Record<ScrapingQueueItem['status'], string>> = {
  processing: 'bg-blue-500/[0.08]',
  retrying: 'bg-amber-500/[0.08]',
};

function QueueItemDetails({ item }: { item: ScrapingQueueItem }) {
  return (
    <div className="grid gap-6 px-6 py-4 md:grid-cols-2">
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Processing
        </h4>
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Retry count</dt>
            <dd className="font-medium">
              {item.retryCount} / {item.maxRetries}
            </dd>
          </div>
          {item.processingStartedAt && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Started</dt>
              <dd className="font-medium">{format(new Date(item.processingStartedAt), 'HH:mm:ss')}</dd>
            </div>
          )}
          {item.processedAt && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Completed</dt>
              <dd className="font-medium">{format(new Date(item.processedAt), 'HH:mm:ss')}</dd>
            </div>
          )}
          {item.extractionConfidence !== undefined && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Confidence</dt>
              <dd className="font-medium">{(item.extractionConfidence * 100).toFixed(0)}%</dd>
            </div>
          )}
          {item.matchScore !== undefined && (
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Match score</dt>
              <dd className="font-medium">{item.matchScore}%</dd>
            </div>
          )}
        </dl>
      </div>

      <div className="space-y-2">
        {item.errorMessage && (
          <>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-destructive">Error</h4>
            <p className="text-sm text-destructive">{item.errorMessage}</p>
          </>
        )}
        {item.scrapedData && (
          <>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Extracted data
            </h4>
            <dl className="space-y-1.5 text-sm">
              {item.scrapedData.title && (
                <div>
                  <dt className="text-muted-foreground">Title</dt>
                  <dd className="font-medium">{item.scrapedData.title}</dd>
                </div>
              )}
              {item.scrapedData.company && (
                <div>
                  <dt className="text-muted-foreground">Company</dt>
                  <dd className="font-medium">{item.scrapedData.company}</dd>
                </div>
              )}
              {item.scrapedData.skills && item.scrapedData.skills.length > 0 && (
                <div>
                  <dt className="text-muted-foreground">Skills</dt>
                  <dd className="mt-1 flex flex-wrap gap-1">
                    {item.scrapedData.skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </>
        )}
      </div>
    </div>
  );
}

export function ScrapingQueueTable({
  items,
  onRetry,
  onDelete,
  onView,
  onStart,
}: ScrapingQueueTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [itemToDelete, setItemToDelete] = useState<ScrapingQueueItem | null>(null);

  // Persistent, non-animated cue: rows in an active state get a faint tint.
  const rowClass = (row: Row<ScrapingQueueItem>) => ACTIVE_ROW[row.original.status] ?? '';

  const columns: ColumnDef<ScrapingQueueItem>[] = [
    {
      id: 'expander',
      header: () => null,
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={row.getToggleExpandedHandler()}
          aria-label="Toggle details"
        >
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              row.getIsExpanded() && 'rotate-180'
            )}
          />
        </Button>
      ),
    },
    {
      accessorKey: 'sourceUrl',
      header: 'Source',
      cell: ({ row }) => {
        const item = row.original;
        const title = deriveJobTitle(item);
        const pretty = prettySourceUrl(item.sourceUrl);
        return (
          <div className="min-w-0 max-w-[340px]">
            <div className="flex items-center gap-2">
              <PlatformBadge url={item.sourceUrl} />
              <span className="truncate font-medium" title={title ?? pretty}>
                {title ?? pretty}
              </span>
              <a
                href={item.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Open source URL"
              >
                <ExternalLink className="size-3.5" />
              </a>
            </div>
            {/* Only show the URL as a secondary line when the primary is a title. */}
            {title && <p className="truncate text-xs text-muted-foreground">{pretty}</p>}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="flex flex-col items-start gap-1">
            <ScrapingStatusBadge status={item.status} />
            {item.status === 'completed' && item.outcome && (
              <ScrapingOutcomeBadge outcome={item.outcome} />
            )}
          </div>
        );
      },
    },
    {
      id: 'job',
      header: 'Job',
      cell: ({ row }) =>
        row.original.jobId ? (
          <Button variant="secondary" size="xs" asChild>
            <Link to="/admin/jobs/$id/details" params={{ id: row.original.jobId }}>
              View
            </Link>
          </Button>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'employee',
      header: 'Posted By',
      cell: ({ row }) => {
        const emp = row.original.employee;
        return (
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{emp.name}</p>
            <p className="truncate text-xs capitalize text-muted-foreground">
              {emp.role.replace('_', ' ')}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => (
        <div className="whitespace-nowrap text-sm">
          <div>{format(new Date(row.original.createdAt), 'MMM d, yyyy')}</div>
          <div className="text-xs text-muted-foreground">
            {format(new Date(row.original.createdAt), 'HH:mm')}
          </div>
        </div>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {item.jobId && (
                <DropdownMenuItem onClick={() => onView?.(item)}>
                  <Eye className="size-4" />
                  View details
                </DropdownMenuItem>
              )}
              {item.status === 'pending' && (
                <DropdownMenuItem onClick={() => onStart?.(item.id)}>
                  <Play className="size-4" />
                  Start scraping
                </DropdownMenuItem>
              )}
              {(item.status === 'failed' ||
                item.status === 'completed' ||
                item.status === 'retrying') && (
                <DropdownMenuItem onClick={() => onRetry?.(item.id)}>
                  <RefreshCw className="size-4" />
                  Retry
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setItemToDelete(item)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: items,
    columns,
    state: { expanded },
    onExpandedChange: setExpanded,
    getRowId: (row) => row.id,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <>
      <DataTable
        table={table}
        rowClassName={rowClass}
        renderSubComponent={(row: Row<ScrapingQueueItem>) => <QueueItemDetails item={row.original} />}
        emptyState={
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Inbox className="size-6 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>No queue items</EmptyTitle>
              <EmptyDescription>
                Scraping jobs will appear here once URLs are queued for extraction.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        }
      />

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scraping Queue Item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this queue item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (itemToDelete) {
                  onDelete?.(itemToDelete.id);
                  setItemToDelete(null);
                }
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
